import {
    Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild
} from '@angular/core';
import {Subscription} from 'rxjs';
import {WidgetData} from 'features/core/shared/widget/widget-data';
import {WidgetDataParam} from 'features/core/shared/widget/widget-data-param';
import {WidgetFactory} from 'features/core/shared/widget/service/widget.factory';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {Sorting} from 'features/core/shared/misc/sorting';
import {QlQueryDto} from 'features/core/shared/ql/data/dto/ql-query.dto';
import {DtoList} from 'features/core/shared/dto/dto.list';
import {MvsWidgetComponent} from 'features/core/shared/widget/component/runtime/mvs-widget/mvs-widget.component';
import {MvsMessageService} from 'features/core/shared/service/message/mvs-message.service';
import {ProvisionAgentStatementDto} from 'features/feature-prov/cp/model/dto/entity/provision-agent-statement.dto';
import {ProvisionStatementStatus} from 'features/feature-prov/cp/enum/provision-statement-status.enum';
import {
    CP_STATEMENT_STATUS_LABELS,
    CP_STATEMENT_STATUS_SEVERITIES
} from 'features/feature-prov/cp/enum/provision-statement-status-label';
import {
    CpProvisionAgentStatementService
} from 'features/feature-prov/cp/service/api/cp-provision-agent-statement.service';
import {cpServerMessage} from 'features/feature-prov/cp/service/domain/cp-server-message';
import {buildCpRunWidgetForm} from 'features/feature-prov/cp/service/domain/cp-run-widget-column';
import {cpRowValue} from 'features/feature-prov/cp/service/domain/cp-provision-assignment-columns';
import {CpWidgetRowObject} from 'features/feature-prov/cp/interface/cp-widget-row-object.interface';

/**
 * AI:
 * Status: "in progress"
 * Type: Component
 * SubType: Shared
 * Reason: navItem "Abrechnungen" of the ProvisionAgent OBC — the stored final statements
 *         (cp.ProvisionAgentStatement, Provisionsabrechnungen) of one agent as a framework widget
 *         table, with Download and "Neu ausstellen" (ACTIVE only) per row.
 *
 * DATA: ONE QL query (entity `cp.ProvisionAgentStatement`, LEFT JOIN `provisionRun` for the run
 * name), fixed filter `provisionAgentId`, sorted `windowToValutaDate desc, version desc` — the newest
 * value date on top, and per run the current version above the superseded ones. No per-row query.
 *
 * WIDGET: `createWidgetTableQl` with a curated `WidgetForm` (fe_core_widget TW-1/TW-2): Nummer, Lauf,
 * Valuta, Version, Status (`#cust_status`, German label + severity tag), Auszahlbar in diesem Lauf,
 * Endsaldo, Aktionen (`#cust_actions`). The widget is created ONCE per agent and reloaded IN PLACE
 * ({@link refresh}, `MvsWidgetComponent.refreshWidget()`), so sorting and paging survive a reissue.
 *
 * ACTIONS:
 *   - Download: `CpProvisionAgentStatementService.downloadDocument$` (the stored PDF in dm), busy per
 *     row, German error toast (the server's sentence when it sent one).
 *   - "Neu ausstellen": only for ACTIVE rows; emits {@link reissueRequested} — the HOST opens the
 *     reason dialog and calls {@link refresh} afterwards (the ONE reload trigger of a reissue). The
 *     right `cp_agentStatement` is enforced by the server (403 → the host demotes {@link canReissue}).
 *
 * STATES: loading (skeleton until the first answer), empty ("Noch keine Abrechnungen"), error
 * (message + retry); a statement without a document never crashes the row (no download button).
 * "Empty" is decided from the UNFILTERED data only ({@link hasStatements}): once the agent was seen
 * with statements, the widget stays visible, and a filter that matches nothing shows the widget's
 * own `noDataText` ("Keine Abrechnungen gefunden") instead of hiding the table with its filter.
 */
@Component({
    selector: 'cp-provision-agent-statements',
    templateUrl: './cp-provision-agent-statements.component.html',
    styleUrls: ['./cp-provision-agent-statements.component.scss'],
    standalone: false
})
export class CpProvisionAgentStatementsComponent implements OnInit, OnChanges, OnDestroy {

    /** Widget alias; also the name of its QL query. */
    static readonly WIDGET_ALIAS: string = 'cp.agent.statements.table.main';

    /** fieldId of the status column; the layout field is `#cust_status`. */
    readonly statusFieldId: string = 'status';

    /** fieldId of the action column; the layout field is `#cust_actions`. */
    readonly actionsFieldId: string = 'actions';

    /** The agent account (cp.ProvisionAgent) whose statements are listed. */
    @Input() provisionAgentId: number;

    /**
     * Optional host answer for the right `cp_agentStatement` ("Neu ausstellen"). Defaults to granted
     * (module pattern of `CpFeatureRight`: optimistic, the server's 403 is authoritative).
     */
    @Input() canReissue: boolean = true;

    /** "Neu ausstellen" clicked on an ACTIVE statement — the host opens the reason dialog. */
    @Output() reissueRequested: EventEmitter<ProvisionAgentStatementDto> =
        new EventEmitter<ProvisionAgentStatementDto>();

    initialized: boolean;

    /** True until the widget delivered its first answer for the current agent. */
    loading: boolean;

    /** The last load failed (the widget answered with an error instead of a list). */
    loadFailed: boolean;

    /**
     * Does the agent have statements at all? `null` = not known yet (first load pending). A load with
     * rows sets it to `true` and nothing sets it back for the same agent — a 0-row load after that is
     * a filter result, not "no statements". While it is `false` the widget is hidden, so no user
     * filter can be active and a 0-row answer is genuinely the unfiltered result.
     */
    hasStatements: boolean | null = null;

    /** The table widget — built once per agent. */
    statementsWidget: WidgetData;

    /** Statement ids with a download in flight. */
    busyStatementIds: Set<number> = new Set<number>();

    /** Mounted widget instance, only for the in-place reload. */
    @ViewChild('statementsWidgetRef') statementsWidgetComponent: MvsWidgetComponent;

    private downloadSubscriptions: Subscription[] = [];

    constructor(protected agentStatementService: CpProvisionAgentStatementService,
                protected messageService: MvsMessageService) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    /** Initialize component. */
    initComponent(): void {
        this.refreshComponent();
        this.initialized = true;
    }

    /** (Re)build the widget for the current agent. */
    refreshComponent(): void {

        this.loadFailed = false;
        this.hasStatements = null;

        if (!this.provisionAgentId) {
            this.statementsWidget = null;
            this.loading = false;
            return;
        }

        this.loading = true;

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Runtime
         * Reason: Stored final statements (Provisionsabrechnungen) of one provision agent — QL on
         *         cp.ProvisionAgentStatement filtered by provisionAgentId, sorted by value date and
         *         version descending; columns number, run, value date, version, status tag, payable
         *         in this run, closing balance, plus #cust_ actions Download / Neu ausstellen.
         */
        this.statementsWidget = WidgetFactory.createWidgetTableQl(
            CpProvisionAgentStatementsComponent.WIDGET_ALIAS,
            'Abrechnungen',
            this.buildStatementsQuery(),
            WidgetDataParam.create('size', 'S'),
            WidgetDataParam.create('widgetForm', buildCpRunWidgetForm([
                {field: 'e.statementNumber', label: 'Nummer'},
                {field: 'pr.name', label: 'Lauf'},
                {field: 'e.windowToValutaDate', label: 'Valuta', outputControl: 'date'},
                {field: 'e.version', label: 'Version'},
                {field: '#cust_' + this.statusFieldId, label: 'Status'},
                {field: 'e.runPayoutAmount', label: 'Auszahlbar in diesem Lauf', outputControl: 'currencyEuro'},
                {field: 'e.closingBalance', label: 'Endsaldo', outputControl: 'currencyEuro'},
                {field: '#cust_' + this.actionsFieldId, label: 'Aktionen'}
            ]))
        );
        // only reached for a filter that matches nothing — "no statements at all" has its own empty state
        this.statementsWidget.noDataText = 'Keine Abrechnungen gefunden';
    }

    /**
     * Reload in place (after a reissue). Keeps the widget instance — sorting and paging survive;
     * builds one only when none exists yet.
     */
    refresh(): void {
        if (this.statementsWidgetComponent && this.statementsWidget) {
            this.loadFailed = false;
            this.statementsWidgetComponent.refreshWidget();
            return;
        }
        this.refreshComponent();
    }

    /**
     * `(onLoadData)` of the widget. The list data provider emits the HTTP error on the same channel
     * as a list (`CoreDpImplList#getData`), so anything without an `entries` array is the error state.
     */
    handleLoadData(dtoList: DtoList | any): void {
        this.loading = false;
        if (!dtoList || !Array.isArray(dtoList.entries)) {
            this.loadFailed = true;
            return;
        }
        this.loadFailed = false;
        this.hasStatements = this.hasStatements === true || dtoList.entries.length > 0;
    }

    /** The dedicated empty state — only when the UNFILTERED data has no statement. */
    get showEmptyState(): boolean {
        return !this.loading && !this.loadFailed && this.hasStatements === false;
    }

    /** The widget is mounted throughout and only hidden while there is nothing it could show. */
    get widgetHidden(): boolean {
        return this.loading || this.loadFailed || this.hasStatements !== true;
    }

    // ── Row helpers (QL keys `<as>.<attribute>`) ───────────────────────────────────────────────

    statementIdOf(row: CpWidgetRowObject): number | null {
        return this.toId(cpRowValue(row, 'e.id'));
    }

    /** Status of a row as the enum — the QL pipe may deliver the ordinal or the constant name. */
    statusOf(row: CpWidgetRowObject): ProvisionStatementStatus | null {
        const value = cpRowValue(row, 'e.status');
        if (typeof value === 'number') {
            return value in ProvisionStatementStatus ? value as ProvisionStatementStatus : null;
        }
        if (typeof value === 'string' && value.trim().length) {
            const normalized = value.trim().toUpperCase();
            const byName = ProvisionStatementStatus[normalized as keyof typeof ProvisionStatementStatus];
            if (byName !== undefined) {
                return byName;
            }
            const ordinal = Number(normalized);
            return Number.isInteger(ordinal) && ordinal in ProvisionStatementStatus
                ? ordinal as ProvisionStatementStatus
                : null;
        }
        return null;
    }

    statusLabelOf(row: CpWidgetRowObject): string {
        const status = this.statusOf(row);
        return status === null ? '–' : CP_STATEMENT_STATUS_LABELS[status];
    }

    statusSeverityOf(row: CpWidgetRowObject): 'success' | 'secondary' {
        const status = this.statusOf(row);
        return status === null ? 'secondary' : CP_STATEMENT_STATUS_SEVERITIES[status];
    }

    isActive(row: CpWidgetRowObject): boolean {
        return this.statusOf(row) === ProvisionStatementStatus.ACTIVE;
    }

    /** A stored statement always has a dm document; a row without one offers no download. */
    hasDocument(row: CpWidgetRowObject): boolean {
        return !!this.statementIdOf(row) && !!this.toId(cpRowValue(row, 'e.dmDocumentId'));
    }

    isDownloadBusy(row: CpWidgetRowObject): boolean {
        const id = this.statementIdOf(row);
        return !!id && this.busyStatementIds.has(id);
    }

    // ── Actions ────────────────────────────────────────────────────────────────────────────────

    /** Download the stored PDF of one statement (blob saved by the service). */
    download(row: CpWidgetRowObject): void {

        const statementId = this.statementIdOf(row);
        if (!statementId || this.busyStatementIds.has(statementId)) {
            return;
        }

        const statementNumber: string = cpRowValue(row, 'e.statementNumber') ?? '';
        this.busyStatementIds.add(statementId);

        const subscription = this.agentStatementService.downloadDocument$(statementId).subscribe({
            next: () => this.busyStatementIds.delete(statementId),
            error: error => {
                this.busyStatementIds.delete(statementId);
                this.showServerError(error, 'Download fehlgeschlagen',
                    'Die Provisionsabrechnung ' + statementNumber + ' konnte nicht geladen werden.');
            },
            complete: () => this.busyStatementIds.delete(statementId)
        });
        this.downloadSubscriptions = this.downloadSubscriptions.filter(existing => !existing.closed);
        this.downloadSubscriptions.push(subscription);
    }

    /** Hand an ACTIVE statement to the host, which opens the reason dialog. */
    requestReissue(row: CpWidgetRowObject): void {
        if (!this.isActive(row) || !this.statementIdOf(row)) {
            return;
        }
        this.reissueRequested.emit(this.toStatementDto(row));
    }

    /** The slim DTO of a row — exactly the fields the QL query reads. */
    protected toStatementDto(row: CpWidgetRowObject): ProvisionAgentStatementDto {
        const dto = new ProvisionAgentStatementDto();
        dto.id = this.statementIdOf(row);
        dto.statementNumber = cpRowValue(row, 'e.statementNumber');
        dto.version = Number(cpRowValue(row, 'e.version')) || null;
        dto.status = this.statusOf(row);
        dto.windowToValutaDate = cpRowValue(row, 'e.windowToValutaDate');
        dto.runPayoutAmount = this.toAmount(cpRowValue(row, 'e.runPayoutAmount'));
        dto.closingBalance = this.toAmount(cpRowValue(row, 'e.closingBalance'));
        dto.provisionRunDtoId = this.toId(cpRowValue(row, 'pr.id'));
        dto.provisionRunDtoName = cpRowValue(row, 'pr.name');
        dto.provisionAgentDtoId = this.provisionAgentId;
        dto.dmDocumentDtoId = this.toId(cpRowValue(row, 'e.dmDocumentId'));
        return dto;
    }

    /** The QL query of the table — a fresh object per widget (the widget mutates its request). */
    protected buildStatementsQuery(): QlQueryDto {
        return {
            name: CpProvisionAgentStatementsComponent.WIDGET_ALIAS,
            type: 'view',
            start: {
                type: 'entity',
                name: 'cp.ProvisionAgentStatement',
                as: 'e',
                fields: [
                    {name: 'id'},
                    {name: 'statementNumber'},
                    {name: 'version'},
                    {name: 'status'},
                    {name: 'windowToValutaDate'},
                    {name: 'runPayoutAmount'},
                    {name: 'closingBalance'},
                    {name: 'dmDocumentId'}
                ],
                joins: [
                    {
                        type: 'entity', name: 'provisionRun', as: 'pr', joinType: 'left',
                        fields: [{name: 'id'}, {name: 'name'}]
                    }
                ]
            },
            fixedFilters: [
                FilterCriteria.create('e.provisionAgentId', FilterCriteria.cOperatorEqual, this.provisionAgentId)
            ],
            sortings: [new Sorting('e.windowToValutaDate', false), new Sorting('e.version', false)]
        };
    }

    /** German error toast: the server's own sentence when it sent one, the fallback otherwise. */
    protected showServerError(error: any, title: string, fallback: string): void {
        cpServerMessage(error)
            .then(message => this.messageService.showErrorMessage(title, message || fallback))
            .catch(() => this.messageService.showErrorMessage(title, fallback));
    }

    private toId(value: any): number | null {
        const parsed = Number(value);
        return value !== null && value !== undefined && value !== '' && Number.isFinite(parsed) && parsed > 0
            ? parsed
            : null;
    }

    private toAmount(value: any): number | null {
        const parsed = Number(value);
        return value !== null && value !== undefined && value !== '' && Number.isFinite(parsed) ? parsed : null;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
        if (changes['provisionAgentId']) {
            this.refreshComponent();
        }
    }

    ngOnDestroy(): void {
        this.downloadSubscriptions.forEach(subscription => subscription.unsubscribe());
    }
}
